/* eslint-disable react/prop-types,max-len */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardContent, Typography, Stack, Button, Box } from '@mui/material';
import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import moment from 'moment';
import lodash from 'lodash';
import PlayAnalysisGroundTruthForm from './play-analysis-ground-truth-form';
import {
  apiUpdateGroundTruth,
  apiSetActionRecognitionTestDatasetFlag,
  apiSetBallBounceDetectionTestDatasetFlag
} from '../ground-truth-updates.api';
import {
  UPDATE_GROUND_TRUTH_SUCCESS,
  setActionRecognitionTestDatasetFlagInGlobalState,
  setBallBounceDetectionTestDatasetFlagInGlobalState,
} from '../ground-truth-updates.reducer';
import { api } from '../../../helpers/api-helper';
import ControlButtons from './control-buttons';
import PlayInfo from './play-info';

const buildPlayClipsObject = (playClips) => {
  // loop through playClips array and create an object with key as ball_delivery_serial_number
  // and value as video_clip_file_path
  return playClips.reduce((acc, clip) => {
    // Remove everything until the last '/' and then remove everything after the last '.' to get the ball delivery number
    const ballDeliveryNumber = clip
      .substring(clip.lastIndexOf('/') + 1, clip.lastIndexOf('.'))
      .replace('play-clip_', '');

    acc[ballDeliveryNumber] = clip;
    return acc;
  }, {});
};

const buildBallDeliverySerialNumberList = (sessionDetails) => {
  // Create a list of ball delivery serial numbers from sessionDetails
  if (!sessionDetails || !Array.isArray(sessionDetails) || sessionDetails.length === 0) {
    return [];
  }

  return sessionDetails.map((detail) => {
    const serialNumber = detail?.ball_delivery_serial_number;
    if (serialNumber) {
      return serialNumber.split('_').pop();
    }
    return null;
  }).filter(Boolean); // Remove null values
};

const PlayAnalysisCard = ({ sessionId, sessionDetails = [], playClips = [], adminUser = false }) => {
  const dispatch = useDispatch();
  const [requestState, setRequestState] = useState(undefined);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [slideOn, setSlideOn] = React.useState(true);
  const [playAnalysisData, setPlayAnalysisData] = useState({
    ballLaunchSequenceNumber: '',
    ballLaunchTimestamp: '',
    videoClipFileName: '',
    videoClipFilePath: '',
    returnShotType: '',
    returnShotTypeGroundTruth: '',
    ball_bounce_location: '',
    ballBounceLocationGroundTruth: '',
    groundTruthLastVerifiedTimestamp: '',
    isActionRecognitionTestDataset: false,
    isBallBounceDetectionTestDataset: false,
  });
  const [openDialog, setOpenDialog] = React.useState(false);
  const [actionRecognitionTestDatasetFlagUpdating, setActionRecognitionTestDatasetFlagUpdating] = React.useState(false);
  const [ballBounceDetectionTestDatasetFlagUpdating, setBallBounceDetectionTestDatasetFlagUpdating] = React.useState(false);
  const ballDeliverySerialNumberList = buildBallDeliverySerialNumberList(sessionDetails);
  const containerRef = React.useRef(null);

  useEffect(() => {
    if (!sessionDetails || sessionDetails.length === 0) {
      return;
    }
    let ballLaunchTimestamp = '';
    if (moment.unix(sessionDetails[currentIndex]?.ball_delivery_event_timestamp).isValid()) {
      ballLaunchTimestamp = moment
        .unix(sessionDetails[currentIndex]?.ball_delivery_event_timestamp)
        .format('MMM DD YYYY, h:mm:ss a');
    }
    const playClipsMap = buildPlayClipsObject(playClips);

    setPlayAnalysisData({
      ballLaunchSequenceNumber: sessionDetails[currentIndex]?.ball_delivery_serial_number ?? '',
      ballLaunchTimestamp,
      videoClipFileName: sessionDetails[currentIndex]?.video_clip_file_name ?? '',
      videoClipFilePath: playClipsMap[sessionDetails[currentIndex]?.ball_delivery_serial_number] ?? sessionDetails[currentIndex]?.video_clip_file_path ?? `https://storage.googleapis.com/wr-prod-ball-machine-static/${sessionDetails[currentIndex]?.video_clip_file_path}`,
      returnShotType: sessionDetails[currentIndex]?.return_shot_type,
      returnShotTypeGroundTruth: sessionDetails[currentIndex]?.return_shot_type_ground_truth,
      ballBounceLocation: sessionDetails[currentIndex]?.ball_bounce_location ?? 'Unknown',
      ballLandedInsideCourtLines: sessionDetails[currentIndex]?.ball_landed_inside_the_court_lines ?? '',
      ballBounceLocationGroundTruth: sessionDetails[currentIndex]?.ball_bounce_location_ground_truth ?? '',
      groundTruthLastVerifiedTimestamp: moment(sessionDetails[currentIndex]?.ground_truth_last_verified_timestamp).isValid() ? moment(sessionDetails[currentIndex]?.ground_truth_last_verified_timestamp)
        .format('MMM DD YYYY, h:mm:ss a') : '',
      isActionRecognitionTestDataset: sessionDetails[currentIndex]?.is_action_recognition_test_data || false,
      isBallBounceDetectionTestDataset: sessionDetails[currentIndex]?.is_ball_bounce_detection_test_data || false,
    });
  }, [currentIndex, sessionDetails, playClips]);

  const navigateToFirst = useCallback(() => {
    setSlideOn(false);
    setCurrentIndex(0);
    setTimeout(() => {
      setSlideOn(true);
    }, 100);
  }, [setCurrentIndex]);

  const navigateToPrevious = useCallback(() => {
    let index = currentIndex - 1;
    if (index < 0) {
      index = 0;
    }
    setSlideOn(false);
    setCurrentIndex(index);
    setTimeout(() => {
      setSlideOn(true);
    }, 100);
  }, [currentIndex, setCurrentIndex]);

  const navigateToNext = useCallback(() => {
    setSlideOn(false);
    setCurrentIndex(currentIndex + 1);
    setTimeout(() => {
      setSlideOn(true);
    }, 100);
  }, [currentIndex, setCurrentIndex]);

  const navigateToLast = useCallback(() => {
    let index = sessionDetails.length - 1;
    if (index < 0) {
      index = 0;
    }
    setSlideOn(false);
    setCurrentIndex(index);
    setTimeout(() => {
      setSlideOn(true);
    }, 100);
  }, [sessionDetails, setCurrentIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        navigateToNext();
      } else if (event.key === 'ArrowLeft') {
        navigateToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigateToNext, navigateToPrevious]);

  const addToActionRecognitionTestDataset = useCallback((event) => {
    const flag = event.target.checked;
    setActionRecognitionTestDatasetFlagUpdating(true);
    const startTime = Date.now();

    apiSetActionRecognitionTestDatasetFlag(
      {
        sessionId,
        ballDeliverySerialNumber: playAnalysisData.ballLaunchSequenceNumber,
        flag: event.target.checked
      })
      .then(result => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(500 - elapsed, 0);
        setTimeout(() => {
          dispatch(setActionRecognitionTestDatasetFlagInGlobalState({
            ballDeliverySerialNumber: playAnalysisData.ballLaunchSequenceNumber,
            isActionRecognitionTestDataset: flag,
          }));
          setActionRecognitionTestDatasetFlagUpdating(false);
        }, delay);
      })
      .catch(() => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(1000 - elapsed, 0);
        setTimeout(() => {
          setActionRecognitionTestDatasetFlagUpdating(false);
        }, delay);
      });
  }, [playAnalysisData]);

  const addToBallBounceDetectionTestDataset = useCallback((event) => {
    const flag = event.target.checked;
    setBallBounceDetectionTestDatasetFlagUpdating(true);
    const startTime = Date.now();

    apiSetBallBounceDetectionTestDatasetFlag(
      {
        sessionId,
        ballDeliverySerialNumber: playAnalysisData.ballLaunchSequenceNumber,
        flag: event.target.checked
      })
      .then(result => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(500 - elapsed, 0);
        setTimeout(() => {
          dispatch(setBallBounceDetectionTestDatasetFlagInGlobalState({
            ballDeliverySerialNumber: playAnalysisData.ballLaunchSequenceNumber,
            isBallBounceDetectionTestDataset: flag,
          }));
          setBallBounceDetectionTestDatasetFlagUpdating(false);
        }, delay);
      })
      .catch(() => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(1000 - elapsed, 0);
        setTimeout(() => {
          setBallBounceDetectionTestDatasetFlagUpdating(false);
        }, delay);
      });
  }, [playAnalysisData]);

  const handleSubmit = async (event, returnShotType, ballBounceLocation) => {
    event.preventDefault();
    setRequestState('in_progress');
    const data = {
      sessionId,
      ballDeliverySerialNumber: playAnalysisData.ballLaunchSequenceNumber,
      returnShotType: returnShotType?.trim() ? returnShotType : 'n/a',
      ballBounceLocation: ballBounceLocation?.trim() ? ballBounceLocation : 'n/a',
    };

    apiUpdateGroundTruth(data)
      .then((result) => {
        const data = lodash.get(result, 'data.result', {});
        setRequestState('success');
        dispatch({
          type: UPDATE_GROUND_TRUTH_SUCCESS,
          payload: {
            data,
          },
        });
        if (currentIndex < sessionDetails.length - 1) {
          setTimeout(() => {
            navigateToNext();
          }, 100);
        }
      })
      .catch(() => {
        setRequestState('failed');
      })
      .finally(() => {
        // Reset the processing state after 2 seconds
        setTimeout(() => {
          setRequestState(undefined);
        }, 2000);
      });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (<Card
        sx={{
          width: '100%',
          minHeight: '500px',
          height: 'auto',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          border: '1px solid #ccc',
        }}
        ref={containerRef}
    >
        <Box sx={{ height: '100%' }}>
            <CardContent
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  height: '100%',
                }}
            >
                <Slide in={slideOn} direction={'left'} container={containerRef.current}>
                    <Box>
                        <PlayInfo
                            ballLandedInsideCourtLines={playAnalysisData.ballLandedInsideCourtLines}
                            ballLaunchTimestamp={playAnalysisData.ballLaunchTimestamp}
                            ballLaunchSequenceNumber={playAnalysisData.ballLaunchSequenceNumber}
                            returnShotType={playAnalysisData.returnShotType}
                            ballBounceLocation={playAnalysisData.ballBounceLocation}
                            groundTruthLastVerifiedTimestamp={playAnalysisData.groundTruthLastVerifiedTimestamp}
                        />
                        {playAnalysisData.videoClipFilePath ? (<Stack direction="column" justifyContent="center">
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                width="100vw"
                                marginTop="1rem"
                            >
                                <Box width={{ xs: '70%' }}>
                                    <video
                                        style={{
                                          width: '100%', height: 'auto',
                                        }}
                                        width="100%"
                                        controls
                                        src={`${api.VIDEO_CLIPS_BASE_URL}/${playAnalysisData.videoClipFilePath}`}
                                        autoPlay={true}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                </Box>
                                <PlayAnalysisGroundTruthForm
                                    playAnalysisData={playAnalysisData}
                                    handleSubmit={handleSubmit}
                                    requestState={requestState}
                                    actionRecognitionTestDatasetFlagUpdating={actionRecognitionTestDatasetFlagUpdating}
                                    addToActionRecognitionTestDataset={addToActionRecognitionTestDataset}
                                    ballBounceDetectionTestDatasetFlagUpdating={ballBounceDetectionTestDatasetFlagUpdating}
                                    addToBallBounceDetectionTestDataset={addToBallBounceDetectionTestDataset}
                                    adminUser={adminUser}
                                />
                            </Stack>
                        </Stack>) : (<Typography
                            sx={{
                              m: 2,
                              flex: 1,
                              marginY: '3rem',
                              textAlign: 'center',
                              color: '#616161',
                              minHeight: '300px',
                            }}
                            variant="h5"
                            component="div"
                        >
                            Play clip not available for this ball delivery.
                        </Typography>)}
                    </Box>
                </Slide>
                <ControlButtons
                    currentIndex={currentIndex}
                    sessionDetails={sessionDetails}
                    navigateToFirst={navigateToFirst}
                    navigateToPrevious={navigateToPrevious}
                    navigateToNext={navigateToNext}
                    navigateToLast={navigateToLast}
                    handleOpenDialog={handleOpenDialog}
                />
            </CardContent>
            <Dialog
                maxWidth="lg"
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle>Select Ball Delivery Serial Number</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select the ball delivery serial number to jump to a specific play clip.
                    </DialogContentText>
                    <Box
                        sx={{
                          display: 'flex', flexDirection: 'column', marginY: '1rem', width: 'fit-content',
                        }}
                    >
                        <Stack direction="row"
                               spacing={2}
                               sx={{
                                 flexWrap: 'wrap',
                                 overflowY: 'auto',
                                 gap: '1rem',
                                 justifyContent: 'flex-end',
                                 alignItems: 'center'
                               }}
                        >
                            {ballDeliverySerialNumberList.map((serialNumber, index) => (<Button
                                key={index}
                                variant={index === currentIndex ? 'contained' : 'outlined'}
                                size="small"
                                disabled={index === currentIndex}
                                onClick={() => {
                                  setCurrentIndex(index);
                                  handleCloseDialog();
                                }}
                                sx={{
                                  textTransform: 'none', color: '#6656F9', borderColor: '#6656F9',
                                }}
                            >
                                {serialNumber}
                            </Button>))}
                        </Stack>

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    </Card>);
};

export default React.memo(PlayAnalysisCard);
